import { Pagination } from '@/components/pagination.tsx'
import { Badge } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Dialog, DialogTrigger } from '@/components/ui/dialog.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.tsx'
import { CreateTagDialog } from '@/routes/_app/admin/-components/create-tag-dialog.tsx'
import { DeleteTagAlertConfirmDialog } from '@/routes/_app/admin/-components/delete-tag-alert-confirm-dialog.tsx'
import { UpdateTagDialog } from '@/routes/_app/admin/-components/update-tag-dialog.tsx'
import { useFetchTagsPaged } from '@/routes/_app/admin/-hooks/use-fetch-tags-paged.ts'
import { EmptyLoadingTable } from '@/routes/_app/syncs/-components/empty-loading-table.tsx'
import { AlertDialog, AlertDialogTrigger } from '@radix-ui/react-alert-dialog'
import { createFileRoute } from '@tanstack/react-router'
import { fallback, zodValidator } from '@tanstack/zod-adapter'
import { Pencil, Search, Tag, Trash } from 'lucide-react'
import { FormEvent, useState } from 'react'
import { z } from 'zod'

export const Route = createFileRoute('/_app/admin/tags')({
  component: Tags,
  validateSearch: zodValidator(
    z.object({
      page: fallback(z.coerce.number().default(1), 1),
      search: z.string().trim().toLowerCase().optional(),
    }),
  ),
})

function Tags() {
  const { page: currentPage, search: searchFromParams } = Route.useSearch()

  const navigate = Route.useNavigate()

  const [search, setSearch] = useState(searchFromParams ?? '')

  const { isLoading, data: results } = useFetchTagsPaged({ page: currentPage, search: searchFromParams })

  function handleSubmitSearch(e: FormEvent) {
    e.preventDefault()

    void navigate({
      search: {
        search: search.trim().toLowerCase(),
      },
    })
  }

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-center text-3xl font-bold tracking-tighter md:text-left">Tags</h1>

      <p className="text-center text-sm text-gray-500 md:text-left">
        Tags são utilizadas para categorizar os conteúdos das obras
      </p>

      <aside className="flex justify-between">
        <form className="flex items-center gap-4" onSubmit={handleSubmitSearch}>
          <Input
            placeholder="Pesquisar tag..."
            className="h-8 w-full max-w-[320px]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button type="submit" variant="ghost" className="h-8">
            <Search className="size-4" />
          </Button>
        </form>

        <Dialog>
          <CreateTagDialog />
          <DialogTrigger asChild>
            <Button variant="outline">
              <Tag className="mr-2 size-4" />
              Adicionar Tag
            </Button>
          </DialogTrigger>
        </Dialog>
      </aside>

      <div className="rounded-sm border">
        <Table>
          <TableHeader>
            <TableHead>Id</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Cor</TableHead>
            <TableHead>Cor</TableHead>
          </TableHeader>
          <TableBody>{isLoading && <EmptyLoadingTable />}</TableBody>

          {results?.data?.map((tag) => (
            <TableRow key={tag.id}>
              <TableCell>{tag.id}</TableCell>
              <TableCell>{tag.name}</TableCell>
              <TableCell>{tag.slug}</TableCell>
              <TableCell>
                <Badge
                  className="text-gray-100"
                  style={{ background: tag.themeColor }}
                  variant="outline"
                  key={tag.id}
                >
                  {tag.color}
                </Badge>
              </TableCell>
              <TableCell>
                <Dialog>
                  <UpdateTagDialog tag={tag} />
                  <DialogTrigger asChild>
                    <Button size="icon" variant="ghost">
                      <Pencil className="size-4 text-muted-foreground" />
                    </Button>
                  </DialogTrigger>
                </Dialog>

                <AlertDialog>
                  <DeleteTagAlertConfirmDialog tag={tag} />
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost">
                      <Trash className="size-4 text-muted-foreground" />
                    </Button>
                  </AlertDialogTrigger>
                </AlertDialog>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </div>

      <div className="flex flex-col items-center justify-between md:flex-row">
        <p className="text-sm text-muted-foreground ">Total de {results?.totalOfPages} tags</p>

        {!search && (
          <div className="flex">
            <Pagination
              totalOfPages={results?.totalOfPages ?? 0}
              currentPage={currentPage}
              handlePageChange={(page) => navigate({ search: { page } })}
            />
          </div>
        )}
      </div>
    </section>
  )
}
